import { useQuery } from '@tanstack/react-query';
import Cookies from 'js-cookie';

import { useApplication } from '@bonusly/application';
import { HttpError } from '@bonusly/http';

import { CSRF_COOKIE_ID } from '../config/csrf';
import { Company } from '../lib/company';
import { useIdentityStore } from '../lib/store';
import { User } from '../lib/user';
import { cleanPath, joinPath } from '../utils/url';

export type MeResponse = {
	user: User;
	company: Company;
	features: Record<string, { enabled: boolean }>;
};

export const meQueryKey = ['identity:me'] as const;

const fetchMe = async (): Promise<MeResponse> => {
	const csrfToken = Cookies.get(CSRF_COOKIE_ID);
	const { token } = useIdentityStore.getState();
	const profileBaseURL = joinPath(useApplication.getState().application.recognizeOrigin, '/api/v2/identity/profile');
	const url = new URL('me', cleanPath(profileBaseURL || ''));

	const response = await fetch(url.toString(), {
		headers: new Headers({
			Authorization: `Bearer ${token}`.trim(),
			...(csrfToken && {
				[CSRF_COOKIE_ID]: csrfToken,
			}),
			'X-BONUSLY-SOURCE': 'internal',
		}),
		credentials: 'include',
	});

	const data = await response.json();

	if (!response.ok) {
		throw new HttpError(response.status, data.redirect_url);
	}

	return data as MeResponse;
};

export const useProfileQuery = () => {
	const token = useIdentityStore((state) => state.token);

	return useQuery({
		queryKey: meQueryKey,
		queryFn: fetchMe,
		enabled: !!token,
		staleTime: Infinity,
	});
};
